import { useEffect, useState, useRef, useCallback } from "react";
import { AdminShell } from "@/components/Layout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Link } from "react-router-dom";

/**
 * LCV Invoice Drop — drag/drop up to 50 PDFs, Gemini OCRs each, admin one-taps
 * to attach each parsed invoice into a WO's PMR Rebill card.
 */
export default function AdminLcvInvoiceDrop() {
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [drops, setDrops] = useState([]);
  const [counts, setCounts] = useState({});
  const fileRef = useRef(null);

  const load = useCallback(() => {
    api.get("/lcv-invoice-drop/queue")
      .then(r => { setDrops(r.data.drops || []); setCounts(r.data.counts || {}); })
      .catch(() => {/* silent */});
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 5000); // Poll while OCR runs in background
    return () => clearInterval(t);
  }, [load]);

  const handleFiles = async (files) => {
    if (!files || !files.length) return;
    const arr = Array.from(files).filter(f => f.type === "application/pdf" || f.name.toLowerCase().endsWith(".pdf"));
    if (!arr.length) { toast.error("PDFs only"); return; }
    if (arr.length > 50) { toast.error("Max 50 files"); return; }
    setUploading(true);

    // Upload one file at a time — a bundled multipart of 4 real invoices can
    // exceed Emergent's default ingress body cap (~10MB) and drops the whole
    // batch. Single-file requests also survive flaky mobile signal.
    let ok = 0;
    let failed = 0;
    const CONCURRENCY = 3;
    const queue = [...arr];
    const worker = async () => {
      while (queue.length) {
        const f = queue.shift();
        if (!f) return;
        // 25MB per-file cap (server also enforces)
        if (f.size > 25 * 1024 * 1024) {
          toast.error(`${f.name}: over 25 MB — skipped`);
          failed++;
          continue;
        }
        try {
          const fd = new FormData();
          fd.append("files", f, f.name);
          await api.post("/lcv-invoice-drop/upload", fd, {
            headers: { "Content-Type": "multipart/form-data" },
            // 90s per file — Gemini parse fires in a background task so this
            // returns as soon as the PDF is stored.
            timeout: 90_000,
          });
          ok++;
        } catch (e) {
          failed++;
          const detail = e?.response?.data?.detail;
          const msg = typeof detail === "string" ? detail : (detail?.message || e?.message || "network error");
          toast.error(`${f.name}: ${msg}`);
        }
      }
    };
    try {
      await Promise.all(Array.from({ length: CONCURRENCY }, worker));
      if (ok > 0) toast.success(`${ok} invoice${ok === 1 ? "" : "s"} uploaded — parsing…`);
      if (failed > 0 && ok === 0) toast.error("All uploads failed — check file sizes or retry");
      load();
    } finally { setUploading(false); }
  };

  return (
    <AdminShell>
      <div className="max-w-5xl mx-auto p-4 sm:p-6" data-testid="admin-lcv-drop">
        <header className="mb-4">
          <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">Rebill Pipeline</div>
          <h1 className="font-display font-black text-3xl text-[#0B1F3A]">LCV Invoice Drop</h1>
          <p className="text-sm text-slate-600 mt-2">
            Drag/drop up to 50 vendor invoice PDFs. Gemini OCRs each one and
            suggests a matching WO — one-tap to populate the PMR Rebill card.
          </p>
        </header>

        {/* Drop zone */}
        <div
          onDragOver={e => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={e => { e.preventDefault(); setDragOver(false); handleFiles(e.dataTransfer.files); }}
          onClick={() => fileRef.current?.click()}
          data-testid="drop-zone"
          className={`p-8 border-4 border-dashed rounded-xl text-center cursor-pointer transition-colors ${
            dragOver
              ? "border-cyan-500 bg-cyan-50"
              : "border-slate-300 bg-white hover:border-cyan-500 hover:bg-cyan-50/40"
          } ${uploading ? "opacity-60 pointer-events-none" : ""}`}
        >
          <div className="text-4xl mb-2">📎</div>
          <div className="font-display font-black text-lg text-[#0B1F3A]">
            {uploading ? "Uploading…" : "Drop LCV invoice PDFs here"}
          </div>
          <div className="text-xs text-slate-500 mt-1">
            or click to select (up to 50 files, 25 MB each)
          </div>
          <input ref={fileRef} type="file" multiple accept="application/pdf,.pdf"
            className="hidden" onChange={e => handleFiles(e.target.files)}
            data-testid="drop-input"/>
        </div>

        {/* Counts */}
        <div className="mt-5 flex flex-wrap gap-2 text-[10px] font-black uppercase tracking-widest">
          <CountPill label="Processing" val={counts.processing} color="amber"/>
          <CountPill label="Parsed" val={counts.parsed} color="cyan"/>
          <CountPill label="Attached" val={counts.attached} color="emerald"/>
          <CountPill label="Error" val={counts.error} color="red"/>
          <CountPill label="Dismissed" val={counts.dismissed} color="slate"/>
        </div>

        {/* Queue */}
        <div className="mt-4 space-y-3">
          {drops.length === 0 && (
            <div className="p-6 text-center text-sm text-slate-400 italic">
              No invoices dropped yet.
            </div>
          )}
          {drops.map(d => <DropRow key={d.id} drop={d} onChanged={load}/>)}
        </div>
      </div>
    </AdminShell>
  );
}

const COLOR_CLS = {
  amber:   "bg-amber-100 text-amber-800 border-amber-300",
  cyan:    "bg-cyan-100 text-cyan-800 border-cyan-300",
  emerald: "bg-emerald-100 text-emerald-800 border-emerald-300",
  red:     "bg-red-100 text-red-800 border-red-300",
  slate:   "bg-slate-100 text-slate-600 border-slate-300",
};
function CountPill({ label, val, color }) {
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 border-2 rounded ${COLOR_CLS[color]}`}
      data-testid={`count-${color}`}>
      {label}: <b className="font-mono">{val || 0}</b>
    </span>
  );
}

function DropRow({ drop: d, onChanged }) {
  const p = d.parsed || {};
  const cands = d.candidates || [];
  const [busy, setBusy] = useState(false);

  const attach = async (jobId) => {
    setBusy(true);
    try {
      await api.post(`/lcv-invoice-drop/${d.id}/attach`, { job_id: jobId });
      toast.success("Attached — open the WO to review + generate PMR bill");
      onChanged();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Attach failed");
    } finally { setBusy(false); }
  };

  const dismiss = async () => {
    if (!window.confirm("Dismiss this drop? You can still re-upload.")) return;
    setBusy(true);
    try {
      await api.post(`/lcv-invoice-drop/${d.id}/dismiss`);
      toast.success("Dismissed");
      onChanged();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Dismiss failed");
    } finally { setBusy(false); }
  };

  const totalLabor = (p.labor || []).reduce((s, r) => s + (r.cost_dollars || 0), 0);
  const totalMat = (p.materials || []).reduce((s, r) => s + (r.cost_dollars || 0), 0);

  return (
    <div className="bg-white border-2 border-[#0f172a] rounded-xl p-4 shadow-[3px_3px_0px_#0f172a]"
      data-testid={`drop-${d.id}`}>
      <div className="flex items-start gap-3 mb-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-black text-sm text-[#0B1F3A] truncate">{d.filename}</span>
            <StatusPill status={d.status}/>
            <a href={`/api/lcv-invoice-drop/pdf/${d.id}`} target="_blank" rel="noreferrer"
              className="text-[10px] font-black uppercase tracking-widest text-cyan-700 hover:underline"
              data-testid={`view-pdf-${d.id}`}>
              📄 View PDF
            </a>
          </div>
          <div className="text-[10px] text-slate-400 mt-0.5">
            Uploaded {d.uploaded_at?.slice(0, 16).replace("T", " ")} by {d.uploaded_by}
          </div>
        </div>
        {d.status !== "dismissed" && d.status !== "attached" && (
          <button onClick={dismiss} disabled={busy}
            data-testid={`dismiss-${d.id}`}
            className="text-[10px] font-black uppercase tracking-widest text-slate-500 hover:text-red-600">
            Dismiss
          </button>
        )}
      </div>

      {d.status === "processing" && (
        <div className="text-xs text-amber-700 italic">🔄 Gemini parsing… (auto-refresh)</div>
      )}
      {d.status === "error" && (
        <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded p-2 mt-1">
          <b>Parse failed:</b> {d.error_message}
        </div>
      )}
      {d.status === "attached" && (
        <div className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded p-2 mt-1">
          ✅ Attached to WO.{" "}
          {d.attached_to_job_id && (
            <Link to={`/admin/jobs/${d.attached_to_job_id}`}
              className="font-black underline"
              data-testid={`open-job-${d.id}`}>
              Open WO →
            </Link>
          )}
        </div>
      )}

      {d.status === "parsed" && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2 text-[11px]">
            <Kv k="Vendor" v={p.vendor_name || "—"}/>
            <Kv k="Invoice #" v={p.invoice_number || "—"}/>
            <Kv k="WO #" v={p.work_order_number || "—"}/>
            <Kv k="Date" v={p.invoice_date || "—"}/>
            <Kv k="Address" v={p.property_address || "—"} span={2}/>
            <Kv k="Labor total" v={`$${totalLabor.toFixed(2)}`}/>
            <Kv k="Materials total" v={`$${totalMat.toFixed(2)}`}/>
          </div>

          {(p.labor || []).length > 0 && (
            <details className="mt-2">
              <summary className="text-[10px] font-black uppercase tracking-widest text-slate-500 cursor-pointer">
                Labor lines ({p.labor.length})
              </summary>
              <ul className="mt-1 space-y-0.5 text-[11px]">
                {p.labor.map((r, i) => (
                  <li key={i}>
                    <span className="uppercase font-black text-slate-500 mr-1">{r.category}</span>
                    {r.description} — <b className="font-mono">${(r.cost_dollars || 0).toFixed(2)}</b>
                  </li>
                ))}
              </ul>
            </details>
          )}
          {(p.materials || []).length > 0 && (
            <details className="mt-1">
              <summary className="text-[10px] font-black uppercase tracking-widest text-slate-500 cursor-pointer">
                Material lines ({p.materials.length})
              </summary>
              <ul className="mt-1 space-y-0.5 text-[11px]">
                {p.materials.map((r, i) => (
                  <li key={i}>{r.description} — <b className="font-mono">${(r.cost_dollars || 0).toFixed(2)}</b></li>
                ))}
              </ul>
            </details>
          )}

          <div className="mt-3 pt-2 border-t border-slate-100">
            <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1.5">
              Candidate WOs {cands.length > 0 ? `(${cands.length})` : ""}
            </div>
            {cands.length === 0 && (
              <div className="text-[11px] text-slate-400 italic">
                No matching WO found. Open a WO manually and paste this invoice link.
              </div>
            )}
            {cands.map((c, i) => (
              <div key={c.job_id} className="flex items-center gap-2 py-1 border-b border-slate-100 last:border-0">
                <span className={`text-[10px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded ${
                  c.confidence >= 0.9 ? "bg-emerald-100 text-emerald-800" : c.confidence >= 0.7 ? "bg-cyan-100 text-cyan-800" : "bg-slate-100 text-slate-600"
                }`}>
                  {(c.confidence * 100).toFixed(0)}%
                </span>
                <span className="font-mono text-xs">{c.wo_number || "—"}</span>
                <span className="text-xs text-slate-600 flex-1 truncate">{c.address}</span>
                <span className="text-[10px] text-slate-400 italic hidden sm:inline">{c.match_reason}</span>
                <Link to={`/admin/jobs/${c.job_id}`}
                  className="text-[10px] font-black uppercase tracking-widest text-slate-500 hover:underline">
                  View
                </Link>
                <button onClick={() => attach(c.job_id)} disabled={busy}
                  data-testid={`attach-${d.id}-${i}`}
                  className="h-7 px-2 bg-[#00C4CC] hover:bg-[#00a8af] text-[#0f172a] hover:text-white text-[10px] font-black uppercase tracking-widest border-2 border-[#0f172a] disabled:opacity-50">
                  Attach
                </button>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function Kv({ k, v, span }) {
  return (
    <div className={span === 2 ? "col-span-2" : ""}>
      <div className="text-[9px] font-black uppercase tracking-widest text-slate-500">{k}</div>
      <div className="font-mono text-slate-800 truncate">{v}</div>
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    processing: ["bg-amber-500 text-white", "PROCESSING"],
    parsed:     ["bg-cyan-500 text-white", "PARSED"],
    attached:   ["bg-emerald-600 text-white", "ATTACHED"],
    error:      ["bg-red-600 text-white", "ERROR"],
    dismissed:  ["bg-slate-400 text-white", "DISMISSED"],
  };
  const [cls, label] = map[status] || ["bg-slate-300 text-slate-800", status];
  return (
    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-black uppercase tracking-widest ${cls}`}>
      {label}
    </span>
  );
}
